// Archetype terrain system — Melee-inspired stage archetypes
export const ARCHETYPE = {
  WEIGHTS: {
    BATTLEFIELD: 2.0,
    ARCHIPELAGO: 0.7,
    TOWER: 0.6,
    ASCENT: 0,  // never randomly selected — forced by mode
  } as Record<string, number>,
  WIDTH: {
    BATTLEFIELD: [1600, 2400] as readonly [number, number],
    ARCHIPELAGO: [1400, 2200] as readonly [number, number],
    TOWER: [1400, 1800] as readonly [number, number],
    ASCENT: [1600, 2000] as readonly [number, number],
  } as Record<string, readonly [number, number]>,
  // Ground surface Y
  GROUND_Y_FRAC: [0.69, 0.82] as readonly [number, number],  // ground surface as fraction of world height (randomized per match, ~993–1180)
  GROUND_DEPTH: 3,              // downward extent (world units) — thin line, sand provides bulk
  // Platform dimensions
  PLATFORM_THICKNESS: 2,        // grid cells (~4px) — thin platform lines
  BEAM_THICKNESS: 1,            // grid cells (~2px)
  NODE_RADIUS: 2,               // grid cells (~4px)
} as const;

// Sand heightmap generation — rolling hills enveloping wireframe skeleton
export const SAND_GEN = {
  TOP_THICKNESS: 35,         // base sand depth above wireframe top (grid cells)
  BOTTOM_THICKNESS: 20,     // sand depth below wireframe bottom (grid cells)
  EDGE_TAPER_CELLS: 12,     // columns at stage edges where sand thins to zero
  SMOOTH_ITERATIONS: 2,     // CA cleanup passes (remove isolated, fill gaps)
  // Multi-octave sine noise for terrain surface
  OCTAVE1_AMP: 25,          // broad rolling hills (grid cells) — big dramatic shapes
  OCTAVE1_FREQ: 0.006,      // low frequency — wide sweeping hills
  OCTAVE2_AMP: 12,          // medium terrain detail
  OCTAVE2_FREQ: 0.018,      // medium frequency
  OCTAVE3_AMP: 5,           // fine roughness
  OCTAVE3_FREQ: 0.05,       // high frequency — small bumps
  MIN_THICKNESS: 3,         // minimum horizontal solid extent to keep sand (cells)
  SURFACE_SMOOTH_PASSES: 3, // neighbor-average passes on heightmap to reduce staircase
} as const;

// Stage sizing — variable terrain width within fixed world
export const STAGE = {
  PLAYER_WIDTH_BONUS: 300,     // extra px per player beyond 2
  MIN_WIDTH: 1000,             // absolute minimum
  MAX_WIDTH: 2400,             // absolute maximum (leave some void on sides)
  SPAWN_MARGIN_FRAC: 0.15,    // spawn points inset 15% from stage edges
} as const;

// Terrain generation — spawn validation
export const TERRAIN_GEN = {
  SPAWN_CLEAR_RADIUS: 20,       // grid cells around spawn to clear
  SPAWN_CLEAR_HEIGHT: 30,       // grid cells above spawn to clear
} as const;

// Structural collapse — wireframe fragment detection after cratering
export const STRUCTURAL = {
  FRAGMENT_THRESHOLD: 8,       // wireframe components ≤ this many cells collapse
  SCAN_EXPAND: 4,              // extra grid cells beyond crater bounds for component scan
  DEBRIS_PER_CELL: 2,          // particles spawned per collapsed wireframe cell
  DEBRIS_CAP: 60,              // max total debris particles per collapse event
  SHAKE_BASE: 4,               // screen shake base intensity
  SHAKE_PER_CELL: 0.3,         // additional shake per collapsed cell
  SHAKE_MAX: 20,               // cap shake intensity
  SHAKE_DECAY: 0.85,           // shake decay rate
} as const;

// Organic cratering
export const CRATER = {
  // Noise defaults (overridden per weapon)
  DEFAULT_PRIMARY_FREQ: 3,
  DEFAULT_PRIMARY_AMP: 0.15,
  DEFAULT_DETAIL_FREQ: 8,
  DEFAULT_DETAIL_AMP: 0.07,
  DEFAULT_FINE_FREQ: 20,
  DEFAULT_FINE_AMP: 0.03,
  DEFAULT_VERTICAL_BIAS: 0,

  // Scorch marks
  SCORCH_RADIUS_MULT: 1.3,
  SCORCH_COLOR: [40, 15, 5] as readonly number[],
  SCORCH_BLEND_MAX: 0.5,
  SCORCH_DECAY_INTERVAL: 0.5,
  SCORCH_DECAY_AMOUNT: 1,

  // Debris particles
  DEBRIS_COUNT_MULT: 0.3,
  DEBRIS_SPEED_MIN: 2,
  DEBRIS_SPEED_MAX: 8,
  DEBRIS_LIFE_MIN: 0.3,
  DEBRIS_LIFE_MAX: 0.8,
  DEBRIS_SCALE_MIN: 0.3,
  DEBRIS_SCALE_MAX: 1.0,
  DEBRIS_GRAVITY: 0.12,
  DEBRIS_FRICTION: 0.96,

  // Hot edges
  HOT_EDGE_COLOR: 0xff4400,
  HOT_EDGE_DECAY: 0.97,
  HOT_EDGE_MAX: 8,
} as const;

// Post-crater smoothing — column-height averaging to smooth jagged crater rims
export const SMOOTHING = {
  FRAMES: 25,                // frames of averaging animation (~0.4s at 60fps)
  BLEND_FACTOR: 0.15,        // per-frame blend toward neighbor average (0-1)
  MAX_REGIONS: 10,           // cap concurrent active regions
  REGION_EXPAND: 3,          // extra grid columns beyond crater bounds
} as const;

// Material-aware rendering palettes (sand vs wireframe)
export const MATERIAL = {
  // Sand fill — warm dark purple (matches existing CIRCUIT fill palette)
  SAND_SURFACE_RGB: [10, 12, 30] as readonly number[],
  SAND_STANDARD_RGB: [5, 5, 16] as readonly number[],
  SAND_MID_RGB: [3, 3, 12] as readonly number[],
  SAND_DEEP_RGB: [2, 2, 8] as readonly number[],

  // Wireframe fill — cool blue-teal, brighter than sand
  WIRE_SURFACE_RGB: [8, 20, 35] as readonly number[],
  WIRE_STANDARD_RGB: [5, 14, 25] as readonly number[],
  WIRE_MID_RGB: [3, 10, 18] as readonly number[],
  WIRE_DEEP_RGB: [2, 6, 12] as readonly number[],

  // Edge glow
  SAND_EDGE_COLOR: 0x00ffcc,       // warm cyan-green
  WIREFRAME_EDGE_COLOR: 0x4488ff,  // cool electric blue
  WIREFRAME_REVEAL_COLOR: 0xddeeff, // bright energy flash on wireframe reveal
  WIREFRAME_REVEAL_SUSTAIN: 0.35,   // power curve exponent — stays bright through explosion, settles after
} as const;
